import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal } from 'antd';
import clsx from 'clsx';
import { useLocation, useNavigate } from 'react-router-dom';

import usePublicAppConfig from '../../hooks/usePublicAppConfig';
import { getPlacementFromPathname } from '../../helper/appInformationPlacement';

import './styles.scss';

const POPUP_DISMISS_PREFIX = 'cadira-app-information-popup-dismissed';

const getPopupDismissKey = (item) =>
	`${POPUP_DISMISS_PREFIX}:${item?.id || 'popup'}`;

const getPopupContent = (item) =>
	String(item?.content || item?.description || '').trim();

const getLinkLabel = (item) => String(item?.linkLabel || 'Lihat Detail').trim();

const getLinkUrl = (item) => String(item?.linkUrl || '').trim();

const getBenefitLines = (item) =>
	getPopupContent(item)
		.split('\n')
		.map((line) => line.trim())
		.filter(Boolean);

const isExternalUrl = (url = '') => /^https?:\/\//i.test(url);

const PopupLinkButton = ({ item, onClick }) => {
	const linkUrl = getLinkUrl(item);

	if (!linkUrl) return null;

	return (
		<Button type="primary" className="app-info-popup__cta" onClick={onClick}>
			{getLinkLabel(item)}
		</Button>
	);
};

const AnnouncementIcon = () => (
	<div className="app-info-popup__icon-container">
		<div className="app-info-popup__icon-ring app-info-popup__icon-ring--1" />
		<div className="app-info-popup__icon-ring app-info-popup__icon-ring--2" />
		<div className="app-info-popup__icon-badge">
			<svg
				width="64"
				height="64"
				viewBox="0 0 64 64"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
				className="app-info-popup__icon-svg"
			>
				<defs>
					<linearGradient
						id="megaphoneGrad"
						x1="12"
						y1="12"
						x2="52"
						y2="52"
						gradientUnits="userSpaceOnUse"
					>
						<stop offset="0%" stopColor="#f36206" />
						<stop offset="100%" stopColor="#ff9a62" />
					</linearGradient>
					<linearGradient
						id="bodyGrad"
						x1="15"
						y1="20"
						x2="45"
						y2="40"
						gradientUnits="userSpaceOnUse"
					>
						<stop offset="0%" stopColor="#175493" />
						<stop offset="100%" stopColor="#2e85e0" />
					</linearGradient>
				</defs>

				{/* Handle */}
				<path
					d="M26 38 L22 48 C21.5 49.2 22.3 50 23.5 50 L31 50 C32 50 32.8 49.2 32.5 48.2 L29.5 38 Z"
					fill="#103c68"
					stroke="#103c68"
					strokeWidth="2"
					strokeLinejoin="round"
				/>
				{/* Back button/cap */}
				<path
					d="M15 26 C15 22 17 20 18 20 L21 20 C22 20 23 21 23 23 L23 37 C23 39 22 40 21 40 L18 40 C17 40 15 38 15 34 Z"
					fill="url(#megaphoneGrad)"
				/>
				{/* Main Cone */}
				<path
					d="M23 23 L42 14 C44 13 46 14.5 46 17 L46 43 C46 45.5 44 47 42 46 L23 37 Z"
					fill="url(#bodyGrad)"
				/>
				{/* Front Ring */}
				<ellipse cx="46" cy="30" rx="3" ry="15" fill="url(#megaphoneGrad)" />

				{/* Sound waves / rays */}
				<path
					d="M53 20 C56 24 56 36 53 40"
					stroke="#f36206"
					strokeWidth="3"
					strokeLinecap="round"
					className="app-info-popup__icon-wave app-info-popup__icon-wave--1"
				/>
				<path
					d="M58 14 C63 20 63 40 58 46"
					stroke="#ff9a62"
					strokeWidth="3"
					strokeLinecap="round"
					className="app-info-popup__icon-wave app-info-popup__icon-wave--2"
				/>
			</svg>
		</div>
	</div>
);

const renderAnnouncement = (item, onCtaClick) => (
	<div className="app-info-popup__body app-info-popup__body--announcement">
		<AnnouncementIcon />
		{item.title ? <h2>{item.title}</h2> : null}
		{getPopupContent(item) ? <p>{getPopupContent(item)}</p> : null}
		<PopupLinkButton item={item} onClick={onCtaClick} />
	</div>
);

const renderFeatureHighlight = (item, onCtaClick) => (
	<div className="app-info-popup__body app-info-popup__body--feature-highlight">
		{item.imageUrl ? (
			<img
				src={item.imageUrl}
				alt={item.title || item.popupTemplateName || 'Informasi Cadira'}
			/>
		) : null}
		<div className="app-info-popup__content">
			<span className="app-info-popup__eyebrow">
				{item.popupTemplateName || 'Feature Highlight'}
			</span>
			{item.title ? <h2>{item.title}</h2> : null}
			{getPopupContent(item) ? <p>{getPopupContent(item)}</p> : null}
			<PopupLinkButton item={item} onClick={onCtaClick} />
		</div>
	</div>
);

const renderBenefitSpotlight = (item, onCtaClick) => {
	const benefitLines = getBenefitLines(item);

	return (
		<div className="app-info-popup__body app-info-popup__body--benefit-spotlight">
			{item.title ? <h2>{item.title}</h2> : null}
			{benefitLines.length ? (
				<ul>
					{benefitLines.map((line) => (
						<li key={line}>{line}</li>
					))}
				</ul>
			) : null}
			<PopupLinkButton item={item} onClick={onCtaClick} />
		</div>
	);
};

const renderImageStory = (item, onCtaClick) => (
	<div className="app-info-popup__body app-info-popup__body--image-story">
		{item.imageUrl ? (
			<img
				src={item.imageUrl}
				alt={item.title || item.popupTemplateName || 'Informasi Cadira'}
			/>
		) : null}
		<div className="app-info-popup__content">
			{item.title ? <h2>{item.title}</h2> : null}
			{getPopupContent(item) ? <p>{getPopupContent(item)}</p> : null}
			<PopupLinkButton item={item} onClick={onCtaClick} />
		</div>
	</div>
);

const renderFullImage = (item) => (
	<div className="app-info-popup__body app-info-popup__body--full-image">
		{item.imageUrl ? (
			<img
				src={item.imageUrl}
				alt={item.title || item.popupTemplateName || 'Informasi Cadira'}
			/>
		) : null}
	</div>
);

const renderPopupContent = (item, onCtaClick) => {
	switch (item.popupTemplate) {
		case 'feature-highlight':
			return renderFeatureHighlight(item, onCtaClick);
		case 'benefit-spotlight':
			return renderBenefitSpotlight(item, onCtaClick);
		case 'image-story':
			return renderImageStory(item, onCtaClick);
		case 'full-image':
			return renderFullImage(item);
		case 'announcement':
		default:
			return renderAnnouncement(item, onCtaClick);
	}
};

const AppInformationPopup = () => {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const publicAppConfig = usePublicAppConfig();
	const [open, setOpen] = useState(false);

	const currentPlacement = useMemo(
		() => getPlacementFromPathname(pathname),
		[pathname]
	);
	const popupItem = useMemo(() => {
		return (publicAppConfig.appInformationItems || []).find((item) => {
			return (
				item?.enabled !== false &&
				item?.informationType === 'popup' &&
				Array.isArray(item?.placement) &&
				item.placement.includes(currentPlacement)
			);
		});
	}, [currentPlacement, publicAppConfig.appInformationItems]);

	const dismissKey = useMemo(() => getPopupDismissKey(popupItem), [popupItem]);

	useEffect(() => {
		if (!popupItem || publicAppConfig.loading) {
			setOpen(false);
			return;
		}

		if (window.sessionStorage.getItem(dismissKey)) {
			setOpen(false);
			return;
		}

		setOpen(true);
	}, [dismissKey, popupItem, publicAppConfig.loading]);

	const closePopup = useCallback(() => {
		if (!popupItem?.closable) return;
		window.sessionStorage.setItem(dismissKey, '1');
		setOpen(false);
	}, [dismissKey, popupItem]);

	const handleCtaClick = useCallback(() => {
		const linkUrl = getLinkUrl(popupItem);
		if (!linkUrl) return;

		if (popupItem?.closable) {
			window.sessionStorage.setItem(dismissKey, '1');
			setOpen(false);
		}

		if (isExternalUrl(linkUrl)) {
			window.location.assign(linkUrl);
			return;
		}

		navigate(linkUrl.startsWith('/') ? linkUrl : `/${linkUrl}`);
	}, [dismissKey, navigate, popupItem]);

	if (!popupItem) return null;

	return (
		<Modal
			centered
			className={clsx(
				'app-info-popup',
				`app-info-popup--${popupItem.popupTemplate || 'announcement'}`
			)}
			closable={popupItem.closable !== false}
			footer={null}
			keyboard={popupItem.closable !== false}
			maskClosable={popupItem.closable !== false}
			onCancel={closePopup}
			open={open}
			width={popupItem.popupTemplate === 'feature-highlight' ? 760 : 520}
		>
			{renderPopupContent(popupItem, handleCtaClick)}
		</Modal>
	);
};

PopupLinkButton.propTypes = {
	item: PropTypes.shape({}).isRequired,
	onClick: PropTypes.func.isRequired,
};

export default AppInformationPopup;
